import { useMemo, useState } from "react";
import { FiArrowRight } from "react-icons/fi";
import pricing from "../data/sero-pricing.json";
import { estimateAccounts, quoteCustom, wonFull, wonMan } from "../data/sero-quote";

// 세로 ERP 견적 계산기 (한국어만). 질문 5개로 예상 계정 수를 잡고, 표준형 가능 여부와 맞춤형 월 이용료를 보여 준다.
const { standard, custom, estimator } = pricing;

const FIELDS = [
  { name: "staff", label: "본사·지점 사무직", unit: "명", hint: "관리자 · 본사 ERP를 쓰는 직원" },
  { name: "directors", label: "장례지도사·의전팀장", unit: "명", hint: "현장 모바일 앱 사용" },
  { name: "hqs", label: "협력 의전본부", unit: "곳", hint: `1곳당 약 ${estimator.accountsPerHq}계정` },
  { name: "vendors", label: "협력업체 (장례식장·납품업체 등)", unit: "곳", hint: `1곳당 약 ${estimator.accountsPerVendor}계정` },
  { name: "clients", label: "기업 경조 계약 고객사", unit: "곳", hint: `1곳당 담당자 약 ${estimator.accountsPerClient}명` },
];

const range = (from, to) => (to == null ? `${from.toLocaleString("ko-KR")}번째부터` : `${from}~${to.toLocaleString("ko-KR")}번째`);

export default function SeroQuoteCalculator({ onConsult }) {
  const [values, setValues] = useState({ staff: 10, directors: 60, hqs: 5, vendors: 10, clients: 20 });
  const est = useMemo(() => estimateAccounts(values), [values]);
  const quote = useMemo(() => quoteCustom(est.admins, est.others), [est]);
  const standardOk = quote.total <= standard.maxUsers;

  const update = (event) => {
    const { name, value } = event.target;
    setValues((current) => ({ ...current, [name]: value === "" ? "" : Math.max(0, Math.min(99999, Number(value))) }));
  };

  const summary = () =>
    [
      "[견적 계산기]",
      ...FIELDS.map(({ name, label, unit }) => `${label}: ${Number(values[name]) || 0}${unit}`),
      `예상 계정: 관리자 ${est.admins}명 + 나머지 ${est.others}명 = ${quote.total}명`,
      `맞춤형 예상 월 이용료: ${wonFull(quote.monthly)} (부가세 별도)`,
    ].join("\n");

  return (
    <div className="serp-quote">
      <div className="serp-quote-inputs">
        {FIELDS.map(({ name, label, unit, hint }) => (
          <label key={name}>
            <span>{label}<small>{hint}</small></span>
            <span className="serp-quote-field">
              <input type="number" inputMode="numeric" min="0" name={name} value={values[name]} onChange={update} />
              <em>{unit}</em>
            </span>
          </label>
        ))}
        <p className="serp-quote-assume">협력업체·고객사 계정 수는 평균값으로 잡은 예상치입니다. 실제 약정 인원은 오픈 후 {custom.trialMonths}개월 동안 쓴 활성 사용자 평균으로 확정합니다.</p>
      </div>

      <div className="serp-quote-result" aria-live="polite">
        <div className="serp-quote-count">
          <small>예상 사용자</small>
          <strong>{quote.total.toLocaleString("ko-KR")}명</strong>
          <span>관리자 {est.admins.toLocaleString("ko-KR")}명 · 나머지 {est.others.toLocaleString("ko-KR")}명 (지도사 {est.directors} · 협력 본부 {est.hqAccounts} · 협력업체 {est.vendorAccounts} · 고객사 {est.clientAccounts})</span>
        </div>

        <div className={`serp-quote-plan${standardOk ? " is-ok" : ""}`}>
          <small>표준형</small>
          <strong>{standardOk ? "무료" : "맞춤형 대상"}</strong>
          <span>{standardOk ? `${standard.maxUsers}명까지 무료로 쓸 수 있습니다 · ${standard.term}` : `표준형은 ${standard.maxUsers}명까지입니다`}</span>
        </div>

        <div className="serp-quote-plan is-main">
          <small>맞춤형 · 초기 비용 0원 · {custom.termMonths}개월</small>
          <strong>월 {wonMan(quote.monthly)}</strong>
          <span>부가세 별도 · 연납하면 {custom.annualFreeMonths}개월 무료 (연 {wonMan(quote.monthly * (12 - custom.annualFreeMonths))})</span>
        </div>

        <table className="serp-quote-lines">
          <tbody>
            {quote.flat ? (
              <tr><th>{custom.flatUsers}명까지 정액</th><td>{wonMan(custom.baseFee)}</td></tr>
            ) : (
              <>
                <tr><th>기본료 <small>관리자 {custom.includedAdmins}명 + 나머지 {custom.includedOthers}명 포함</small></th><td>{wonMan(custom.baseFee)}</td></tr>
                {quote.adminLines.map((line) => (
                  <tr key={`a${line.from}`}><th>관리자 {range(line.from, line.to)} <small>{line.people}명 × {wonFull(line.price)}{line.discount ? ` (${line.discount}% 할인)` : ""}</small></th><td>{wonMan(line.amount)}</td></tr>
                ))}
                {quote.otherLines.map((line) => (
                  <tr key={`o${line.from}`}><th>나머지 {range(line.from, line.to)} <small>{line.people.toLocaleString("ko-KR")}명 × {wonFull(line.price)}{line.discount ? ` (${line.discount}% 할인)` : ""}</small></th><td>{wonMan(line.amount)}</td></tr>
                ))}
              </>
            )}
            <tr className="is-total"><th>월 이용료{quote.discountRate > 0 ? <small>정가 {wonMan(quote.listPrice)} 대비 {quote.discountRate}% 할인</small> : null}</th><td>{wonMan(quote.monthly)}</td></tr>
            <tr><th>{custom.termMonths}개월 총액</th><td>{wonMan(quote.monthly * custom.termMonths)}</td></tr>
          </tbody>
        </table>

        <button type="button" className="serp-quote-cta" onClick={() => onConsult?.(summary())}>이 인원으로 상담 신청 <FiArrowRight /></button>
      </div>
    </div>
  );
}
